import { ApplitoolsProvider } from './ApplitoolsProvider';
import { LambdaTestProvider } from './LambdaTestProvider';
import type { IVisualTestingProvider, VisualTestingBatchCleanup } from './IVisualTestingProvider';
import type { ApplitoolsIntegrationData } from 'clickim/src/common/api/testimVisualTestingApi';
import type { RunContext } from '../stepActions/stepAction';
import type { ExtendedContext } from 'clickim/src/background/eyesUtils';

export type VisualTestingProviderType = 'applitools' | 'lambdatest';

export interface ActiveProviderContext {
    runContext: RunContext;
    context: ExtendedContext;
}

export interface ActiveProviderResult {
    provider: IVisualTestingProvider;
    providerType: VisualTestingProviderType;
}

export function getActiveVisualTestingProvider(ctx: ActiveProviderContext): ActiveProviderResult {
    const { runContext, context } = ctx;

    if (runContext.smartUIConnected && runContext.smartUIIntegrationData) {
        const provider = new LambdaTestProvider({
            credentials: runContext.smartUIIntegrationData,
            buildId: runContext.testResultId,
            testName: context.testName,
            projectId: runContext.projectId,
            branch: runContext.currentBranch,
        });
        return { provider, providerType: 'lambdatest' };
    }

    if (runContext.applitoolsConnected && runContext.applitoolsIntegrationData) {
        const sdkConfig = context.applitoolsSdkConfig as { batch?: { id?: string } } | undefined;
        const batchId = sdkConfig?.batch?.id || context.testResultId;

        const provider = new ApplitoolsProvider({
            credentials: runContext.applitoolsIntegrationData,
            buildId: batchId,
            applitoolsSdkConfig: context.applitoolsSdkConfig,
            applitoolsSdkLogger: context.applitoolsSdkLogger,
            shouldUseVisualGrid: context.shouldUseVisualGrid,
            concurrency: context.config?.applitoolsConcurrency || 5,
        });
        return { provider, providerType: 'applitools' };
    }

    throw new Error('No visual testing provider connected');
}

export async function getBatchCleanup(projectId: string): Promise<VisualTestingBatchCleanup | null> {
    const testimServicesApi = await import('../../commons/testimServicesApi');
    const applitoolsData = await testimServicesApi.getVisualTestingIntegrationData(projectId, 'applitools');
    if (applitoolsData && 'runKey' in applitoolsData) {
        return ApplitoolsProvider.createBatchCleanup(applitoolsData as ApplitoolsIntegrationData);
    }
    return null;
}
